/**
 * Base Registry class for extensible content registration
 *
 * Registries enable adding new content (items, biomes, upgrades) without
 * modifying core code. Content is validated on registration and stored
 * frozen to prevent accidental mutation.
 *
 * @example
 * const ItemRegistry = new Registry('Items');
 * ItemRegistry.addValidator((id, def) => {
 *     if (!def.name) return 'Item must have a name';
 * });
 * ItemRegistry.register('sword', { name: 'Sword', damage: 10 });
 */
export class Registry {
    /**
     * Create a new registry
     * @param {string} name - Registry name (used in error messages)
     */
    constructor(name) {
        this._name = name;
        this._items = new Map();
        this._validators = [];
    }

    /**
     * Register a definition with the registry
     * @param {string} id - Unique identifier
     * @param {Object} definition - Definition object
     * @returns {this} The registry instance for chaining
     * @throws {Error} If validation fails
     */
    register(id, definition) {
        // Run all validators
        for (const validator of this._validators) {
            const error = validator(id, definition);
            if (error) {
                throw new Error(`[${this._name}] Validation failed for '${id}': ${error}`);
            }
        }

        // Warn on duplicate (allows override for dev)
        if (this._items.has(id)) {
            console.warn(`[${this._name}] Overwriting existing definition: '${id}'`);
        }

        // Store frozen copy with id included
        this._items.set(id, Object.freeze({ id, ...definition }));
        return this;
    }

    /**
     * Get a definition by ID
     * @param {string} id - The definition ID
     * @returns {Object} The frozen definition object
     * @throws {Error} If ID not found
     */
    get(id) {
        const item = this._items.get(id);
        if (!item) {
            throw new Error(`[${this._name}] Unknown id: '${id}'`);
        }
        return item;
    }

    /**
     * Get all registered definitions
     * @returns {Array<Object>} Array of all frozen definition objects
     */
    getAll() {
        return Array.from(this._items.values());
    }

    /**
     * Check if an ID exists in the registry
     * @param {string} id - The definition ID
     * @returns {boolean} True if ID exists
     */
    has(id) {
        return this._items.has(id);
    }

    /**
     * Add a validation function
     * Validators receive (id, definition) and return error string or null
     * @param {Function} fn - Validator function
     * @returns {this} The registry instance for chaining
     */
    addValidator(fn) {
        this._validators.push(fn);
        return this;
    }

    /**
     * Clear all registered items (useful for testing)
     */
    clear() {
        this._items.clear();
    }

    /**
     * Get the number of registered items
     * @returns {number} Count of registered items
     */
    get size() {
        return this._items.size;
    }
}
